import {
  Component,
  Input,
  EventEmitter,
  Output,
  ViewChild,
  ElementRef,
  SimpleChanges,
  OnInit,
  OnChanges,
} from '@angular/core'
import { FormBuilder, Validators, FormGroup } from '@angular/forms'
import get from 'lodash-es/get'
import find from 'lodash-es/find'
import 'moment-timezone'
import moment from 'moment'
import { ValidationService } from '../../../services/validation-service'
import { infoToast, errorToast } from '../../../toaster'
import { externalRoute } from '../external-routes'
import {
  IAccount,
  IKeyword,
  IContentType,
  ICustomer,
  KeywordQueries,
  IWriter,
  IPriceList,
  IProduct,
} from '../../../graphql/queries/keyword.queries'
import { paymentIcon } from 'logic/pricing'
import { waitUntil, quantityChargeDollars } from '../../../utils'
import { TinymceConfig, tinymceConfig } from 'src/tinymce'
import { MultipassSeoRequirementsComponent } from 'modules/keywords/keyword-form/multipass-seo-requirements.component'
import { MultipassDeadlineComponent } from 'modules/keywords/keyword-form/multipass-deadline.component'
import { MultipassDirectAssignmentComponent } from 'modules/keywords/keyword-form/multipass-direct-assignment.component'
import { MultipassWordLengthDropdownComponent } from 'modules/keywords/keyword-form/multipass-word-length-dropdown/multipass-word-length-dropdown.component'
import {
  hasViableDeadline,
  getViableDeadline,
  getMinDeadlineDate,
  getMaxDeadlineDate,
} from '../utils'
import { availableCredits } from 'logic/subscription-states'
import pluralize from 'pluralize'
import {
  PostCreditPayment,
  PaymentOption,
  KeywordFormPaymentOptionType,
} from 'interfaces/post-credit-payment.interface'
import { EnhancementAddOn } from './enhancement-addons.enum'
import { EnhancementAlertHelper } from 'src/modules/shared/enhancement-alert.helper'
import { placeholderString } from './help-strings'
import { DeliveryMethod } from 'src/modules/delivery-options/enums'
import sweetAlert from 'src/bmAlert'
import { KeywordMutations } from 'src/graphql/mutations/keyword.mutations'
import { DeclineSubmissionsHelper } from '../helpers/decline-submissions-alerts.helper'

@Component({
  styles: [
    `
      :host {
        display: block;
      }
      .btn-outline-muted {
        border-color: #ced4da !important;
      }
      .highlighted {
        background: #f1ff36;
        padding: 0 6px 0 2px;
        color: #444;
        font-weight: 600;
      }
    `,
  ],
  templateUrl: './multipass-keyword-form.component.html',
  selector: 'multipass-keyword-form',
})
export class MultipassKeywordFormComponent
  implements OnInit, OnChanges, PostCreditPayment<KeywordFormPaymentOptionType>
{
  @Input() keyword: IKeyword = null
  @Input() account: IAccount = null // Deadline flipper feature & billing info
  @Input() customer: ICustomer = null
  @Input() allContentTypes: IContentType[] = null
  @Input() allowedContentTypes: IContentType[] = null
  @Input() priceList: IPriceList = null

  @Input() contentTypeId: number
  @Input() duplicateId: number = null
  @Input() submitting: boolean

  @Output() create: EventEmitter<{ body: object; files: File[] }> = new EventEmitter<{
    body: object
    files: File[]
  }>()
  @Output() duplicate: EventEmitter<{ body: object; documents: any[] }> = new EventEmitter<{
    body: object
    documents: any[]
  }>()
  @Output() update: EventEmitter<object> = new EventEmitter<object>()
  @Output() cancel: EventEmitter<null> = new EventEmitter<null>()
  @Output() delete: EventEmitter<null> = new EventEmitter<null>()
  @Output() startDuplication: EventEmitter<null> = new EventEmitter<null>()

  @ViewChild('editorEl') editorEl: ElementRef<any>
  @ViewChild('pickerAnchor') pickerAnchor: ElementRef<any>
  @ViewChild('seoRequirementsComp')
  seoRequirements: MultipassSeoRequirementsComponent
  @ViewChild('deadlineComp') deadlineDialog: MultipassDeadlineComponent
  @ViewChild('directAssignmentComp')
  directAssignmentDialog: MultipassDirectAssignmentComponent
  @ViewChild('wordLengthDropdown')
  wordLengthDropdown: MultipassWordLengthDropdownComponent
  tempFiles: File[] = []

  deadlinePickerVisible: boolean = false
  calendarVisible: boolean = false
  editingPostLimit: boolean = false
  editingSeoRequirements: boolean = false
  showSeoRequirements: boolean = false
  photoDisabled: boolean = null
  prevPhotoSelected: boolean = false
  optimizeIsDisabled = false
  assignedWriter: IWriter
  videoModalVisible = false
  enhancementAddOn = EnhancementAddOn

  tinymcePlaceholder = ''

  tinymceConfig: TinymceConfig = tinymceConfig(<TinymceConfig>{
    placeholder: this.tinymcePlaceholder,
    toolbar: 'bold italic underline | numlist bullist | link | blockquote',
    statusbar: true,
    elementpath: false,
    resize: true,
    height: 440,
  })

  form: FormGroup
  tempSeoRequirements: FormGroup
  // Need to use more "temporary" placeholders instead of direct form binding, because user needs to save choice

  selectedContentType: IContentType = null
  selectedProduct: IProduct = null

  minDeadlineDate: Date
  maxDeadlineDate: Date

  priorities: string[] = ['Low Priority', 'Medium Priority', 'High Priority']
  paymentOptions: PaymentOption<KeywordFormPaymentOptionType>[] = []
  selectedPaymentOption: PaymentOption<KeywordFormPaymentOptionType> = null

  constructor(
    private fb: FormBuilder,
    private keywordQueries: KeywordQueries,
    private keywordMutations: KeywordMutations
  ) {}

  ngOnInit(): void {
    this.optimizeIsDisabled = this.customer.postDeliveryMethod === DeliveryMethod.HubSpot
    if (this.duplicateId) {
      this.keywordQueries
        .duplicateKeywordPageQuery(this.customer.id, +this.duplicateId)
        .subscribe(({ data }) => {
          if (data && !this.keyword) {
            // do this only once
            this.keyword = data.customer.keyword
            this.resetForm().then(() => {
              this.keyword.id = null // New kw
              this.form.markAsDirty()
              this.updatePaymentOptions()
              const defaultOptionType = this.paymentOptions.find(
                (o) => !o.optionDisabled
              ).optionType
              this.selectPaymentOption(defaultOptionType)
            })
          }
        })
    } else {
      this.resetForm()
    }
    this.waitForPlaceholder()
      .then((_el) => {
        this.changeContentType(this.form.value.contentTypeId)
      })
      .catch((e) => console.log('Timed out waiting for TinyMCE placeholder', e))

    this.changeProduct(this.form.value.product.id, false)
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty('submitting')) return
    this.resetForm().then(() => {
      const postLimit = this.form.value.postLimit
      if (this.customer && postLimit) {
        this.updatePaymentOptions()
        const paymentOptions = this.paymentOptions
          .filter((o) => !o.optionDisabled)
          .map((o) => o.optionType)
        const preferredPaymentOption =
          paymentOptions.find((o) => o === 'credits') ||
          paymentOptions.find((o) => o === 'mixed-payment') ||
          paymentOptions.find((o) => o === 'charge')
        this.selectPaymentOption(preferredPaymentOption)
      }
    })
  }
  waitForPlaceholder = waitUntil(() => document.querySelector('editor placeholder'), 100, 2000)

  get preassignedWriter(): IWriter {
    const assignedKeyword = get(this.keyword, 'kind') === 'assigned'
    const writer = get(this.keyword, 'writer')
    return assignedKeyword && writer
  }

  resetForm(): Promise<void> {
    const defaults = {
      kind: get(this.keyword, 'kind', 'open'),
      phrase: this.duplicateId
        ? '[Copy of] ' + get(this.keyword, 'phrase', '')
        : get(this.keyword, 'phrase', ''),
      description: get(this.keyword, 'description', ''),
      deadline: this.duplicateId
        ? this.getSensibleDuplicateDeadline(this.keyword)
        : get(this.keyword, 'deadline')
        ? new Date(this.keyword.deadline)
        : null,
      timeZone: this.deadlineTimeZone(),
      priority: get(this.keyword, 'priority', 1),
      state: get(this.keyword, 'state', 'active'),
      postLimit: get(this.keyword, 'postLimit', 1) || 1,
      contentTypeId: this.contentTypeId,
      focusKeywords: get(this.keyword, 'focusKeywords', ''),
      link: get(this.keyword, 'link', ''),
      linkText: get(this.keyword, 'linkText', ''),
      externalLinks: get(this.keyword, 'externalLinks', null),
      searcherIntent: get(this.keyword, 'searcherIntent', ''),
      seoNotes: get(this.keyword, 'seoNotes', ''),
      contentType: get(this.keyword, 'contentTypeId', 1),
      product: get(this.keyword, 'product', {}),
      rewrite: get(this.keyword, 'rewrite', false),
      writerId: this.duplicatingCurrentKeyword() ? null : get(this.keyword, 'writerId', null),
      allowInsecureLinks: get(this.keyword, 'allowInsecureLinks', false),
      requireSameTitle: get(this.keyword, 'requireSameTitle', false) || false,
      quantityFromCredits: 0,
      optimize: this.defaultAddOns('optimize'),
      photo: this.defaultAddOns('optimize') || this.defaultAddOns('photo'),
      video: this.defaultAddOns('video'),
    }
    this.form = this.fb.group({
      kind: [defaults.kind, Validators.required],
      phrase: [defaults.phrase, Validators.required],
      description: defaults.description,
      deadline: defaults.deadline,
      timeZone: defaults.timeZone,
      priority: defaults.priority,
      state: [defaults.state, Validators.required],
      postLimit: defaults.postLimit,
      contentTypeId: defaults.contentTypeId,
      product: defaults.product,
      writerId: defaults.writerId,
      quantityFromCredits: defaults.quantityFromCredits,

      // SEO Options
      focusKeywords: defaults.focusKeywords,
      link: [defaults.link, ValidationService.urlValidator],
      linkText: defaults.linkText,
      externalLinks: defaults.externalLinks,
      searcherIntent: defaults.searcherIntent,
      seoNotes: defaults.seoNotes,
      rewrite: defaults.rewrite,
      allowInsecureLinks: defaults.allowInsecureLinks,
      requireSameTitle: defaults.requireSameTitle,

      //PRO SERVICES
      optimize: [
        {
          value: defaults.optimize,
          disabled: this.customer.postDeliveryMethod === DeliveryMethod.HubSpot,
        },
      ],
      photo: defaults.photo,
      video: defaults.video,
    })
    this.prevPhotoSelected = this.form.get('photo').value
    this.changeAddons('optimize')
    this.changeContentType(this.form.value.contentTypeId)
    this.changeProduct(this.form.value.product.id, false)
    this.setDeadlineBuffers()

    if (this.keyword && this.keyword.kind === 'assigned' && this.keyword.writer)
      this.deadlinePickerVisible = true

    return Promise.resolve()
  }

  defaultAddOns(code: string): boolean {
    if (this.keyword) {
      return this.keyword.product?.addOns?.some((addon) => addon.code === code)
    }
    return this.customer.subscription.productsAtActivation?.some((p) => p === code)
  }

  handleAlreadyPaid(): number {
    if (this.duplicateId || this.keyword?.state !== 'fulfilled') return 0

    return this.keyword?.multipassCreditReserved || 0
  }

  calculateCreditTotal(): number {
    let valueInCredits = this.selectedProduct?.valueInCredits || 0
    let optimizeChecked = this.form.get('optimize').value

    this.selectedProduct?.addOns.forEach((addon) => {
      // Do not add the photo price if +optimize is selected
      if (this.form.value[addon.code] && !(optimizeChecked && addon.code == 'photo')) {
        valueInCredits += addon.valueInCredits
      }
    })

    // if is duplicate or not fulfilled, alreadyPaid is 0 in order to discount the total of credits
    const alreadyPaid = this.handleAlreadyPaid()

    const totalCredits =
      valueInCredits * (this.form.value.postLimit - (this.keyword?.alreadyPurchasedPosts || 0)) -
      alreadyPaid

    return Math.round(totalCredits)
  }

  updatePaymentOptions(): void {
    let neededCredits = this.calculateCreditTotal()
    let customerBalance = this.customer.balance

    let mixedPaymentCredits = neededCredits
    let mixedPaymentAmount = 0
    if (neededCredits > customerBalance) {
      mixedPaymentCredits = customerBalance
      mixedPaymentAmount = neededCredits - customerBalance
    }

    const mixedPaymentTotalAmount = this.getTotalAmount(mixedPaymentAmount)
    const neededCreditsTotalAmount = this.getTotalAmount(neededCredits) || '$0'

    const mixedPaymentDisabled = this.form.value.postLimit < this.customer.postCreditsCount
    const creditsBackMsg =
      'You can always deactivate or delete your request to receive credits back.'
    const paypal = this.account.billingType === 'paypal'

    this.paymentOptions = [
      {
        optionType: 'credits',
        optionDisabled: false,
        optionText: `${neededCredits} ${pluralize('credit', neededCredits)}`,
        optionDescription: this.optionValidForSelection('credits')
          ? 'Save 5% by using subscription credits'
          : 'Not enough credits for this method',
        optionPlaceholder: `${neededCredits} ${pluralize('credit', neededCredits)}`,
        confirmMessage:
          `You will pay ${neededCredits} ${pluralize('credit', neededCredits)}. ` + creditsBackMsg,
        quantityFromCredits: neededCredits,
      },
      {
        optionType: 'mixed-payment',
        optionDisabled: true,
        optionText: `${mixedPaymentCredits} ${pluralize('credit', mixedPaymentCredits)} + charge ${
          mixedPaymentTotalAmount ?? '$0'
        }`,
        optionDescription: (() => {
          if (mixedPaymentDisabled) {
            return 'Mixed payment not available'
          } else if (paypal) {
            return `Spend your credits + charge the remainder to linked PayPal account.`
          } else {
            return `Spend your credits + charge the remainder to card ending ${this.account.lastFour}`
          }
        })(),
        optionPlaceholder: `${mixedPaymentCredits} ${pluralize(
          'credit',
          mixedPaymentCredits
        )} + ${mixedPaymentTotalAmount}`,
        confirmMessage:
          (paypal
            ? `${mixedPaymentCredits} ${pluralize(
                'credit',
                mixedPaymentCredits
              )} + charge ${mixedPaymentTotalAmount} to linked <i class="${this.paymentIcon()}"></i> <b>PayPal</b> account. `
            : `${mixedPaymentCredits} ${pluralize(
                'credit',
                mixedPaymentCredits
              )} + charge ${mixedPaymentTotalAmount} to <i class="${this.paymentIcon()}"></i> ending in <b>${
                this.account.lastFour
              }</b>. `) + creditsBackMsg,
        quantityFromCredits: mixedPaymentCredits,
      },
      {
        optionType: 'charge',
        optionDisabled: false,
        optionText: `${neededCreditsTotalAmount}`,
        optionDescription: paypal
          ? `One-time charge to linked PayPal account`
          : `One-time charge to card ending ${this.account?.lastFour}`,
        optionPlaceholder: neededCreditsTotalAmount,
        confirmMessage:
          (paypal
            ? `Charge ${neededCreditsTotalAmount} to linked <i class="${this.paymentIcon()}"></i> <b>PayPal</b> account. `
            : `Charge ${neededCreditsTotalAmount} to <i class="${this.paymentIcon()}"></i> ending in <b>${
                this.account.lastFour
              }</b>. `) + creditsBackMsg,
        quantityFromCredits: 0,
      },
    ]

    this.paymentOptions.forEach(
      (o) => (o.optionDisabled = !this.optionValidForSelection(o.optionType))
    )
  }

  getTotalAmount(pieces: number): string {
    const creditPriceInCents = this.priceList.aLaCarteCreditPriceInCents
    return quantityChargeDollars(pieces, creditPriceInCents)
  }

  isEditable(): boolean {
    return (
      this.newKeyword() ||
      this.duplicateId !== null ||
      !['draft', 'deleted', 'fulfilled'].includes(this.keyword?.state)
    )
  }

  onStartDuplication(): void {
    this.form.markAsPristine()
    this.startDuplication.emit()
  }

  selectText(): void {
    if (this.htmlToText(this.form.value.description) === this.htmlToText(this.tinymcePlaceholder)) {
      const ed = get(this.editorEl, 'editor', null)
      if (ed) {
        if (ed.selection) {
          setTimeout(() => {
            ed.selection.select(ed.getBody())
          }, 0)
        }
      }
    }
  }

  populatePlaceholderTemplate(populate: boolean): void {
    const isDescriptionSameAsPlaceholder =
      this.htmlToText(this.tinymcePlaceholder) === this.htmlToText(this.form.value.description)

    if (populate && (!this.form.value.description || isDescriptionSameAsPlaceholder)) {
      this.form.patchValue({ description: this.tinymcePlaceholder })
      this.selectText()
    } else if (!populate && this.tinymcePlaceholder && isDescriptionSameAsPlaceholder) {
      this.form.controls.description.reset('')
    }
  }

  // DEADLINES

  shouldShowDeadlinePicker(): boolean {
    return this.deadlineFeature() || (this.keyword && this.keyword.deadline !== null)
  }

  getSensibleDuplicateDeadline(keyword: IKeyword): Date {
    if (!keyword || !keyword.deadline) return null
    const deadline = new Date(keyword.deadline)
    const deadlineBufferHours = this.customer.deadlineBufferHours
    if (hasViableDeadline(keyword, deadlineBufferHours)) {
      return deadline
    } else {
      return getViableDeadline(deadlineBufferHours)
    }
  }

  deadlineTimeZone(): string {
    return get(this.keyword, 'timeZone', moment.tz.guess() || 'America/Denver')
  }

  closeDeadlineDialog(): void {
    if (!this.assignedWriter) this.deadlinePickerVisible = false
  }

  removeDeadline(): void {
    this.form.value.deadline = null
    this.form.patchValue({ deadline: null })
  }

  // DIRECT ASSIGNMENTS

  shouldShowDirectAssignmentsPicker(): boolean {
    return this.directAssignmentsEnabled || (this.keyword && this.keyword.writer !== null)
  }

  onAssignWriter(writer: IWriter): void {
    this.assignedWriter = writer
    if (this.deadlineDialog && !this.form.value.deadline) this.deadlineDialog.onShowDeadlinePicker()
    this.deadlinePickerVisible = true
  }

  removeAssignment(): void {
    this.assignedWriter = null

    // need to maintain deadline claimer if deadline keyword
    const assignedKeyword = this.keyword?.kind === 'assigned'
    this.form.patchValue({
      writer: assignedKeyword ? null : this.keyword?.writer,
      writerId: assignedKeyword ? null : this.keyword?.writerId,
    })

    this.deadlinePickerVisible = false
  }

  get assignedWithoutDeadline(): boolean {
    return !!this.assignedWriter && !this.form.value.deadline
  }

  // END DIRECT ASSIGNMENTS

  private htmlToText(html: string): string {
    const div = document.createElement('div')
    div.innerHTML = html
    return (div.textContent || div.innerText || '').replace(/\n|\r|\s+/g, '').trim()
  }

  onePost(): boolean {
    return this.form.value.postLimit === 1
  }

  multiplePosts(): boolean {
    return this.form.value.postLimit !== 1
  }

  activating(newState: string): boolean {
    const activeStates = ['active', 'assigned']
    return activeStates.includes(newState) && !activeStates.includes(this.keyword.state)
  }

  reactivatingKeyword(): boolean {
    return this.keyword && !['active', 'paused', 'assigned'].includes(this.keyword.state)
  }

  updatingCurrentKeyword(): boolean {
    return this.keyword && ['active', 'paused', 'assigned'].includes(this.keyword.state)
  }

  duplicatingCurrentKeyword(): boolean {
    return this.keyword && this.duplicateId !== null
  }

  viewingKeyword(): boolean {
    return this.keyword && this.duplicateId === null
  }

  newKeyword(): boolean {
    return !this.keyword || !this.keyword.id
  }

  // a new assignment is one that needs to be accepted by the writer
  newAssignment(): boolean {
    const w = this.assignedWriter
    return (
      w &&
      (this.newKeyword() ||
        this.keyword.writerId !== w.id ||
        ['paused', 'declined', 'auto_declined'].includes(this.keyword.state))
    )
  }

  async onSaveDraft(): Promise<void> {
    const cancelConfirm = await DeclineSubmissionsHelper.showDeactivateRequestSweetAlert()
    if (cancelConfirm && this.keyword && this.keyword.id && this.keyword.queuedCount > 0) {
      const confirmDeclineSubmissions =
        await DeclineSubmissionsHelper.showDeclineInactiveSubmissionsSweetAlert(
          this.keyword.queuedCount
        )
      if (confirmDeclineSubmissions)
        this.keywordMutations.declineRemainingKeywordSubmissions({
          id: this.keyword.id,
        })
    }
    if (cancelConfirm) this.onSubmit('paused')
  }

  onSubmit(newState: string = 'active'): void {
    this.submitting = true
    let isActivatingButActivationIsDisabled = newState !== 'paused' && this.activationDisabled
    let isDraftButNotEditable = newState === 'paused' && !this.isEditable()

    if (isActivatingButActivationIsDisabled || isDraftButNotEditable) {
      infoToast('Activation of this request is not allowed')
      this.submitting = false
      return
    }

    // Tried reactivating before incrementing to postLimit
    this.form.patchValue({
      state: newState,
      priority: Number.parseInt(this.form.value.priority),
    })
    if (this.assignedWriter) {
      this.form.patchValue({
        kind: 'assigned',
        writerId: this.assignedWriter.id,
        priority: 1,
      })
      // this line is VERY sensitive logic - change with caution!
      if (
        newState === 'active' &&
        (this.newAssignment() ||
          (this.keyword && ['assigned', 'active', 'fulfilled'].includes(this.keyword.state)))
      ) {
        this.form.patchValue({ state: 'assigned' })
      }
    } else if (this.form.value.deadline) {
      this.form.patchValue({ kind: 'deadline', priority: 1 })
    } else {
      this.form.patchValue({ kind: 'open' })
    }

    // include addons in the prodect
    let selectedAddons: { id: number; quantity: number }[] = []
    this.selectedProduct?.addOns.forEach((addon) => {
      if (this.form.value[addon.code]) {
        selectedAddons.push({
          id: addon.addOnId,
          quantity: 1,
        })
      }
    })
    this.form.patchValue({
      product: {
        id: this.selectedProduct?.id,
        addOns: selectedAddons,
      },
    })
    if (this.newKeyword()) {
      this.onCreate()
    } else if (this.reactivatingKeyword() || this.updatingCurrentKeyword()) {
      this.onUpdate()
    }
  }

  onCreate(): void {
    const { optimize, photo, video, ...body } = this.form.value
    if (this.duplicateId) {
      const createObj = {
        body,
        documents: this.keyword.documents,
        files: this.tempFiles,
        selectedPaymentOption: this.selectedPaymentOption,
      }
      this.duplicate.emit(createObj)
    } else {
      const createObj = {
        body,
        files: this.tempFiles,
        selectedPaymentOption: this.selectedPaymentOption,
      }
      this.create.emit(createObj)
    }
  }

  onUpdate(): void {
    const { optimize, photo, video, ...body } = this.form.value
    const updateObj = {
      body,
      selectedPaymentOption: this.selectedPaymentOption,
    }
    this.update.emit(updateObj)
  }

  onDelete(): void {
    this.delete.emit(null)
  }

  // Post limit
  validatePiecesLimit(e: Event | number): void {
    const postLimit = e instanceof Event ? parseInt((e.target as HTMLInputElement).value) : e

    if (postLimit >= (this.keyword?.alreadyPurchasedPosts || 1) && postLimit < 100) {
      this.setPostLimit(postLimit)
    } else if (postLimit >= 100) {
      errorToast('Cannot be greater than 99')
      this.setPostLimit(99)
    } else {
      errorToast(`Cannot be less than ${this.keyword?.alreadyPurchasedPosts || 1}`)
      this.setPostLimit(1)
    }

    this.updatePaymentOptions()
    if (this.selectedPaymentOption) {
      this.selectPaymentOption(this.selectedPaymentOption.optionType)
    } else if (postLimit <= this.customer?.postCreditsCount) {
      const defaultOptionType = this.paymentOptions.find((o) => !o.optionDisabled).optionType
      this.selectPaymentOption(defaultOptionType)
    }
  }

  setPostLimit(postLimit: number) {
    if (postLimit > 1) {
      this.removeAssignment()
      this.removeDeadline()
    }

    this.form.patchValue({ postLimit })
  }

  selectPaymentOption(selectedOptionType: KeywordFormPaymentOptionType): void {
    if (!this.optionValidForSelection(selectedOptionType)) {
      this.selectedPaymentOption = null
      this.form.patchValue({ quantityFromCredits: null })
      return
    }

    this.selectedPaymentOption = this.paymentOptions.find((o) => o.optionType == selectedOptionType)

    let quantityFromCredits
    switch (this.selectedPaymentOption?.optionType) {
      case 'charge':
        quantityFromCredits = 0
        break
      case 'credits':
        quantityFromCredits = this.paymentOptions[0].quantityFromCredits
        break
      case 'mixed-payment':
        quantityFromCredits = this.paymentOptions[1].quantityFromCredits
        break
      default:
        quantityFromCredits = null
    }

    this.form.patchValue({ quantityFromCredits })
  }

  optionValidForSelection(selectedOptionType: KeywordFormPaymentOptionType): boolean {
    let valueInCredits = this.calculateCreditTotal()
    let neededCredits = this.customer.multipass ? valueInCredits : this.form.value.postLimit
    const numCredits = availableCredits(this.customer)

    switch (selectedOptionType) {
      case 'charge':
        return neededCredits > 0
      case 'credits':
        return numCredits >= neededCredits
      case 'mixed-payment':
        return numCredits > 0 && numCredits < neededCredits
    }
  }

  paymentIcon(): string {
    return paymentIcon(this.account)
  }

  updatePriority(newPriority: number): void {
    this.form.patchValue({ priority: newPriority })
  }

  // Can't use reactive forms. Angular sometimes can't tell on data reloads that tinymce is in the frame
  updateDescription(newDescription: string): void {
    const descriptionChanged = newDescription !== this.form.get('description').value
    this.form.patchValue({ description: newDescription })
    if (descriptionChanged) this.form.controls.description.markAsDirty()
  }

  onCancel(): void {
    this.cancel.emit(null)
  }

  linkTo(externalRouteKey: string): string {
    return externalRoute(externalRouteKey)
  }

  deadlineFeature(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return features.includes('deadlines') || features.includes('direct_assignments')
  }

  changeContentType(newContentTypeId: number): void {
    if (!this.form.value.description) this.form.patchValue({ description: '' })
    this.form.patchValue({ contentTypeId: newContentTypeId })
    this.selectedContentType = find(this.allContentTypes, {
      id: newContentTypeId,
    })
    this.tinymcePlaceholder = placeholderString[newContentTypeId] ?? null
    const placeHolderEl = document.querySelector('editor placeholder')
    if (placeHolderEl) placeHolderEl.innerHTML = this.tinymcePlaceholder
    if ([1, 4].includes(newContentTypeId)) {
      this.showSeoRequirements = true
    } else {
      if (this.showSeoRequirements) {
        this.seoRequirements.resetSeoRequirements()
        this.seoRequirements.onSubmitSeoRequirements()
        this.showSeoRequirements = false
      }
    }
  }

  changeProduct(newProductId: number, markFormAsDirty: boolean): void {
    this.form.patchValue({ product: { id: newProductId } })
    this.selectedProduct = find(this.priceList?.products, { id: newProductId })

    if (markFormAsDirty) {
      this.form.markAsDirty()
      this.validatePiecesLimit(this.form.value.postLimit)
    }
  }

  changeAddons(addOnCode): void {
    if (addOnCode === 'optimize') {
      if (this.form.get('optimize').value) {
        this.photoDisabled = true
        this.prevPhotoSelected = this.form.get('photo').value
        this.form.patchValue({ photo: true })
      } else {
        this.photoDisabled = null
        this.form.patchValue({ photo: this.prevPhotoSelected })
      }
    }
    this.updatePaymentOptions()
    const defaultOptionType = this.paymentOptions.find((o) => !o.optionDisabled).optionType
    this.selectPaymentOption(defaultOptionType)
  }

  getNumberOfPhotos(): string {
    if (!this.selectedProduct) return 'stock photos'

    switch (this.selectedProduct.words) {
      case 300:
      case 600:
        return '1 stock photo'
      case 1000:
      case 1500:
        return '4 stock photos'
      case 2000:
      case 3000:
      case 4000:
        return '6 stock photos'
      default:
        return '1 stock photo'
    }
  }

  contentTypeIsAllowed(contentTypeId: number): boolean {
    return this.allowedContentTypes.some((type) => {
      return type.id === contentTypeId
    })
  }

  productIsAllowed(productId: number): boolean {
    return true
  }

  toggleVideoModalVisibility() {
    this.videoModalVisible = !this.videoModalVisible
  }

  openEnhancementsModal($event: Event, addOnCode: string): void {
    EnhancementAlertHelper.openEnhancementsModal($event, addOnCode, () =>
      this.toggleVideoModalVisibility()
    )
  }

  get contentTypesEnabled(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return (
      features.includes('content_types') &&
      (get(this.customer, 'multipass', false) || get(this.customer, 'plan.words', false))
    )
  }

  get wordCountsEnabled(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return (
      this.customer.multipass &&
      features.includes('content_types') &&
      get(this.priceList, 'products', null)
    )
  }

  get directAssignmentsEnabled(): boolean {
    const features = (this.account && this.account.enabledFeatures) || []
    return features.includes('direct_assignments')
  }

  get draftButtonText(): string {
    const keywordState = get(this.keyword, 'state', null)
    switch (keywordState) {
      case 'active':
      case 'assigned':
        return this.duplicateId ? 'Save & exit' : 'Deactivate'
      case 'fulfilled':
      case 'deleted':
        return 'Save as Draft'
      // 'paused' or creating new keyword
      default:
        return 'Save & exit'
    }
  }

  get draftHelperText(): string {
    const keywordState = get(this.keyword, 'state', null)
    switch (keywordState) {
      case 'active':
      case 'assigned':
        return 'This request is active and open for writer submissions. Deactivate to stop receiving new content.'
      case 'fulfilled':
      case 'deleted':
        return 'Drafts are Inactive and visible only to you and your team. Activate to request submissions from writers.'
      // 'paused' or creating new keyword
      default:
        return 'Progress will be saved as Inactive, visible only to you and your team. Activate to request submissions from writers.'
    }
  }

  get activationDisabled(): boolean {
    return (
      (this.selectedContentType && !this.contentTypeIsAllowed(this.selectedContentType.id)) ||
      this.hasClaimedFulfilledKeyword ||
      this.assignedWithoutDeadline ||
      !this.hasValidDeadline ||
      !this.selectedPaymentOption ||
      !this.selectedProduct ||
      this.form.value.phrase?.trim() == ''
    )
  }

  get saveDisabled(): boolean {
    return this.form.value.phrase?.trim() == ''
  }

  get hasValidDeadline(): boolean {
    if (
      this.form.value.deadline === null ||
      (!this.deadlineChanged && this.keyword.state !== 'paused' && !this.reactivatingKeyword())
    ) {
      return true
    }
    const minDate = new Date(this.minDeadlineDate)
    const minDateInt = minDate.setHours(minDate.getHours() - 1)
    const minDateAllowed = this.form.value.deadline > minDateInt
    const maxDateAllowed = this.form.value.deadline < this.maxDeadlineDate

    return minDateAllowed && maxDateAllowed
  }

  get deadlineChanged(): boolean {
    const formDeadline = this.form.value.deadline ? this.form.value.deadline.getTime() : null
    const keywordDeadline = this.keyword?.deadline ? this.keyword.deadline.getTime() : null

    return formDeadline !== keywordDeadline
  }

  get changingPostLimit(): boolean {
    return this.keyword ? this.keyword.postLimit != this.form.value.postLimit : true
  }

  get hasClaimedFulfilledKeyword(): boolean {
    return !!(this.keyword?.state === 'fulfilled' && this.keyword?.writer?.id && !this.duplicateId)
  }

  setDeadlineBuffers(): void {
    if (!this.customer) return
    const buffer = this.customer.deadlineBufferHours
    this.minDeadlineDate = getMinDeadlineDate(buffer)
    this.maxDeadlineDate = getMaxDeadlineDate(buffer)
  }
}
